import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onSuccess", "skip"],
  _excluded2 = ["data", "previousData"];
import { registerQuery, useQuery } from 'data-fetching-client';
import { ViewMeta } from 'crm-index-visualization-core/core/model/viewMeta';
import { tryOr } from 'crm-index-visualization-core/core/error/errorUtils';
import { ViewDraft } from 'crm-index-visualization-core/core/model/viewDraft';
import { useClientStorage, useDraftContext } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { useMemo } from 'react';
import { useView } from './useView';
import { pageTypeVar } from '../reactiveVar/currentPageType';
// Internal query - now exported for cache updates
export const getViewMetaQueryInternal = registerQuery({
  fieldName: 'vizcoreViewMeta',
  fetcher: async ({
    draftContext,
    clientStorage,
    view
  }) => {
    // Detect bypass mode from the hash key
    const isBypassMode = draftContext.editHash === 'bypass-draft-active';
    const pageType = pageTypeVar();
    if (isBypassMode) {
      return new ViewMeta(Object.assign({}, view, {
        currentViewVisualization: pageType !== null && pageType !== void 0 ? pageType : view.currentViewVisualization
      }));
    }

    // If we have an editHash, try to get draft data from client cache first
    // this edit hash is currently bound to our CURRENT VIEW. It cannot support new
    if (draftContext.editHash) {
      const {
        viewMetaHash
      } = await tryOr({}, () => ViewDraft.fromClientCache(draftContext.editHash, clientStorage));
      if (viewMetaHash) {
        const draftViewMeta = await tryOr(null, () => ViewMeta.fromClientCache(viewMetaHash, clientStorage));
        if (draftViewMeta) {
          return draftViewMeta;
        }
      }
    }

    // Fall back to using cached view data
    return new ViewMeta(Object.assign({}, view, {
      currentViewVisualization: pageType !== null && pageType !== void 0 ? pageType : view.currentViewVisualization
    }));
  },
  args: ['draftContext', 'clientStorage', 'view']
});
const EMPTY_OBJECT = {};

/**
 * Hook to fetch current view metadata with transparent draft data handling.
 *
 * Built on Data Fetching Client (DFC) to provide ViewMeta focused on identity, ownership,
 * and permissions. Automatically returns draft data when editHash is present, falls back
 * to backend view data otherwise.
 *
 * @param options.onSuccess Callback for successful data fetch
 * @param options.onError Callback for handling errors
 * @param options.skip Whether to skip the query
 * @param options.variables Override query variables (draftContext)
 * @returns {{ data: ViewMeta | null, loading: boolean, error?: ApolloError }}
 *
 * @example
 * ```tsx
 * const { data: viewMeta, loading, error } = useViewMeta();
 * if (viewMeta?.isEditableBy(userId, scopes)) {
 *   // Show edit controls
 * }
 * ```
 *
 * @see {@link ViewMeta} For ViewMeta model properties and methods
 * @see {@link useMutateViewMeta} For editing view metadata
 * @see README.md For complete API documentation and usage patterns
 * @see [core/model/view-meta.ts](../model/view-meta.ts) For ViewMeta class implementation details
 */
export function useViewMeta(options = EMPTY_OBJECT) {
  var _options$variables$dr, _options$variables;
  const [globalDraftContext] = useDraftContext();
  const clientStorage = useClientStorage();

  // Determine the effective draft context (local override or global)
  const effectiveDraftContext = (_options$variables$dr = (_options$variables = options.variables) === null || _options$variables === void 0 ? void 0 : _options$variables.draftContext) !== null && _options$variables$dr !== void 0 ? _options$variables$dr : globalDraftContext;
  const {
    data: {
      vizcoreCurrentView
    } = {},
    loading: viewLoading,
    error: viewError
  } = useView({
    onError: options.onError,
    viewId: effectiveDraftContext.viewId,
    objectTypeId: effectiveDraftContext.objectTypeId
  });
  const {
      onSuccess,
      skip
    } = options,
    restOptions = _objectWithoutPropertiesLoose(options, _excluded);
  const shouldSkip = skip || !effectiveDraftContext.editHash && !vizcoreCurrentView;

  // We keep the DFC field key internal and don't expose it to consumers
  const _useQuery = useQuery(getViewMetaQueryInternal, Object.assign({}, restOptions, {
      skip: !!shouldSkip,
      onCompleted: responseData => {
        if (responseData !== null && responseData !== void 0 && responseData.vizcoreViewMeta && onSuccess) {
          onSuccess(responseData.vizcoreViewMeta);
        }
      },
      variables: {
        draftContext: effectiveDraftContext,
        clientStorage,
        view: vizcoreCurrentView
      }
    })),
    {
      data,
      previousData
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded2);
  return useMemo(() => {
    // Transform the data to remove the DFC field key
    const transformedData = (data === null || data === void 0 ? void 0 : data.vizcoreViewMeta) || (previousData === null || previousData === void 0 ? void 0 : previousData.vizcoreViewMeta) || null;

    // If there's a view error, bubble it up as the view meta error
    const finalError = viewError !== null && viewError !== void 0 ? viewError : rest.error;

    // Combine loading states: show loading if either view or view meta query is loading
    const isLoading = viewLoading || rest.loading;
    return Object.assign({
      data: transformedData
    }, rest, {
      loading: isLoading,
      error: finalError
    });
  }, [data, previousData, rest, viewError, viewLoading]);
}